import { Config } from "@planetscale/database";
import { and, eq, isNull } from "drizzle-orm";
import { redirect } from "react-router";
import { z } from "zod";
import { formatDate } from "~/components/site/Date";
import { LinkButton } from "~/components/site/links/LinkButton";
import { MdContent } from "~/components/site/MdContent";
import { UserLink } from "~/components/site/UserLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { tt, tturl, useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, OurLoaderArgs } from "~/util/Loaders";
import { loadMarkdown } from "~/util/Markdown.server";
import { Slug } from "~/util/Slug";
import { logPageView } from "~/util/stats";
import { generateUrl, getTitle, wikiPage } from "~/util/wiki";

const Params = z.object({ pageId: z.string() });

async function makeData(args: OurLoaderArgs, cfg: Config) {
  const t = tt.bind(null, args.realm.lang);
  const params = Params.parse(args.params);
  const [idStr] = params.pageId.split("-", 2);

  if (!idStr) {
    return redirect("/wiki");
  }

  const page = await pg.db
    .select({
      id: pg.mdPages.id,
      slug: pg.mdPages.wikiSlug,
      game: pg.mdPages.game,
      createdAt: pg.mdPages.createdAt,
      revision: {
        id: pg.mdRevisions.id,
        titleEn: pg.mdRevisions.titleEn,
        contentEn: pg.mdRevisions.contentEn,
        titleRu: pg.mdRevisions.titleRu,
        contentRu: pg.mdRevisions.contentRu,
        createdAt: pg.mdRevisions.createdAt,
      },
    })
    .from(pg.mdPages)
    .where(
      and(
        eq(pg.mdPages.id, parseInt(params.pageId)),
        eq(pg.mdPages.isDeleted, false),
        isNull(pg.mdPages.npcName),
        isNull(pg.mdPages.itemName),
        isNull(pg.mdPages.locName),
        isNull(pg.mdPages.questId),
        isNull(pg.mdPages.skillId),
        isNull(pg.mdPages.draftId)
      )
    )
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
    .then((res) => res.at(0));

  if (!page) {
    throw Response.json(
      { msg: t({ en: "Page not found.", ru: "Страница не найдена." }) },
      { status: 404 }
    );
  }

  // Log
  logPageView(args);
  //

  const idealPath = tturl(
    args.realm,
    `/wiki/${generateUrl(page?.id, page?.slug)}`
  );
  if (idealPath !== args.url.pathname) {
    throw redirect(idealPath, 301);
  }

  const contributors = await pg.db
    .selectDistinct({
      id: pg.users.id,
      name: pg.users.login,
      avatar: pg.users.avatar,
    })
    .from(pg.mdRevisions)
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(eq(pg.mdRevisions.pageId, parseInt(idStr)));

  return {
    page: {
      ...page,
      revision: {
        ...page.revision,
        contentEnWithMarkdown: await loadMarkdown(
          page.revision.contentEn ?? "",
          args.gd.all_gd!,
          args.realm.lang,
          args.realm.game
        ),
        contentRuWithMarkdown: await loadMarkdown(
          page.revision.contentRu ?? "",
          args.gd.all_gd!,
          args.realm.lang,
          args.realm.game
        ),
      },
      contributors,
      url: Slug(page.id + " " + page.slug),
    },
  };
}

$PAGE({
  path: "wiki/:pageId",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => {
    const { t, data } = cx;
    const { page } = data;
    const title = `${t(
      getTitle(page.revision.titleEn, page.revision.titleRu)
    )} - ${t(wikiPage)} - L2Hub.info`;
    return { title };
  },
  render: ({ data }) => {
    const { page } = data;
    const t = useT();
    const turl = useTurl();
    const realm = useRealm();

    const pageTitle = t(getTitle(page.revision.titleEn, page.revision.titleRu));
    return (
      <div className="content">
        <div className="columns">
          <div className="column is-three-quarters">
            <Breadcrumb
              items={[
                {
                  title: t(wikiPage),
                  url: turl("/wiki"),
                },
                { title: pageTitle, url: turl(`/wiki/${page.url}`) },
              ]}
            />
            <PageTitle>{pageTitle}</PageTitle>
            {realm.lang === "ru" ? (
              <MdContent>{page.revision.contentRuWithMarkdown}</MdContent>
            ) : (
              <MdContent>{page.revision.contentEnWithMarkdown}</MdContent>
            )}
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "Information",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: t({ en: "Version", ru: "Версия" }),
                    value: data.page.game && data.page.game.join(", "),
                  },
                  {
                    name: t({ en: "Created At", ru: "Создано" }),
                    value: formatDate(page.createdAt),
                  },
                  {
                    name: t({ en: "Updated At", ru: "Обновлено" }),
                    value: formatDate(page.revision.createdAt),
                  },
                ]}
              />
              <div style={{ marginTop: 15 }}>
                <LinkButton to={turl(`/wiki/${page.id}/edit`)}>
                  {t({ en: "Edit Page", ru: "Редактирвоать" })}
                </LinkButton>
              </div>
            </InfoBox>

            <InfoBox
              title={t({
                en: "Contributors",
                ru: "Составители",
              })}
            >
              <div
                style={{ display: "flex", gap: 10, flexDirection: "column" }}
              >
                {page.contributors.map((user) => (
                  <UserLink key={user.id} user={user} />
                ))}
              </div>
            </InfoBox>
          </div>
        </div>
      </div>
    );
  },
});
